import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@chakra-ui/react';
import { useEmbeddedEffect } from '@machinemetrics/mm-react-embeddable';
import i18n from 'i18next';

import MMDrawer from 'components/MMDrawer';
import MMEmbeddableZoneWithLoader from 'components/MMEmbeddableZoneWithLoader';
import {
  DRAWER_HEADER_EVENT,
  DRAWER_CLOSE_EVENT,
  DRAWER_SUBMIT_EVENT,
} from 'lib/embedMessages';

/**
 * Host shell for embedded MFE drawers: MMDrawer + zone + loader + standard
 * lifecycle/header message listeners.
 */
const EmbeddedMMDrawer = ({
  embedZoneId,
  messageSource,
  src,
  path,
  context,
  isOpen,
  onDismiss,
  onSubmit,
  defaultTitle,
  zoneParams,
}) => {
  const [title, setTitle] = useState(defaultTitle);
  const [closeAction, setCloseAction] = useState('dismiss');
  const [hideHostHeader, setHideHostHeader] = useState(false);
  const [drawerBackAt, setDrawerBackAt] = useState(0);

  useEffect(() => {
    if (!isOpen) {
      setTitle(defaultTitle);
      setCloseAction('dismiss');
      setHideHostHeader(false);
      setDrawerBackAt(0);
    }
  }, [isOpen, defaultTitle]);

  useEmbeddedEffect(
    embedZoneId,
    `${messageSource}:${DRAWER_HEADER_EVENT}`,
    useCallback((payload) => {
      if (payload?.title != null) {
        setTitle(payload.title);
      }
      if (payload?.closeAction) {
        setCloseAction(payload.closeAction);
        if (payload.closeAction === 'dismiss') {
          setDrawerBackAt(0);
        }
      }
      if (payload?.hideHostHeader != null) {
        setHideHostHeader(Boolean(payload.hideHostHeader));
      }
    }, [])
  );

  useEmbeddedEffect(
    embedZoneId,
    `${messageSource}:${DRAWER_CLOSE_EVENT}`,
    useCallback(() => {
      onDismiss();
    }, [onDismiss])
  );

  useEmbeddedEffect(
    embedZoneId,
    `${messageSource}:${DRAWER_SUBMIT_EVENT}`,
    useCallback(
      (result) => {
        if (onSubmit) {
          onSubmit(result);
        }
      },
      [onSubmit]
    )
  );

  const handleDrawerClose = useCallback(() => {
    if (closeAction === 'back') {
      setDrawerBackAt(Date.now());
      return;
    }
    onDismiss();
  }, [closeAction, onDismiss]);

  const zoneContext = useMemo(() => {
    if (!context) {
      return null;
    }
    return { ...context, drawerBackAt };
  }, [context, drawerBackAt]);

  const resolvedZoneParams = useMemo(() => {
    return {
      language: i18n.language,
      ...zoneParams,
    };
  }, [zoneParams]);

  return (
    <MMDrawer
      isOpen={isOpen}
      onClose={handleDrawerClose}
      title={title}
      hideHeader={hideHostHeader}
      closeAction={closeAction}
    >
      <Box
        flex="1"
        minH={0}
        display="flex"
        flexDirection="column"
        overflow="hidden"
        h="100%"
      >
        {zoneContext && (
          <MMEmbeddableZoneWithLoader
            id={embedZoneId}
            src={src}
            path={path}
            context={zoneContext}
            params={resolvedZoneParams}
          />
        )}
      </Box>
    </MMDrawer>
  );
};

EmbeddedMMDrawer.propTypes = {
  embedZoneId: PropTypes.string.isRequired,
  messageSource: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  context: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  defaultTitle: PropTypes.string.isRequired,
  zoneParams: PropTypes.object,
};

export default EmbeddedMMDrawer;
